/**
 * Query keys de platform — transversales: catálogo comercial, cumplimiento, archivo documental y selectores genéricos.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const platformKeys = {
  companyBusinessContext: (companyId?: string, userId?: string, accessScope?: string) =>
    ['company-business-context', companyId, userId, accessScope] as const,

  // Catálogo de planes (GET /api/plans) — tenant-agnóstico, sin companyId.
  plans: {
    all: () => ['plans-catalog'] as const,
  },

  // Uso del plan de la empresa activa (GET /api/plans/usage) — SÍ tenant-scoped
  // (medidores de facturas/OCR/bancos/asientos/IA de la pestaña Plan, Fase 2).
  planUsage: (companyId?: string) => ['plan-usage', companyId] as const,

  // Packs de créditos comprables (GET /api/billing/packs). La tarifa la sirve
  // Stripe, no el backend, y es la misma para todos — pero la ruta pide `owner`
  // de la empresa activa, así que la clave lleva `companyId`: al cambiar de
  // empresa, lo cacheado bajo la anterior no describe lo que ahora se puede ver.
  billingPacks: (companyId?: string) => ['billing-packs', companyId] as const,

  // ── Fase 8 / Bloque R — Cumplimiento ───────────────────────────────────────
  compliance: {
    all: () => ['compliance'] as const,
    auditLogs: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['compliance', 'audit-logs', companyId, filters] as const,
    retention: (companyId?: string) => ['compliance', 'retention', companyId] as const,
  },

  // Archivo documental (tabla `documents`). El listado filtra en SERVIDOR desde
  // 2026-07 — los filtros forman parte de la clave para no servir de caché el
  // resultado de otro filtro.
  document: {
    all: () => ['documents'] as const,
    list: (companyId?: string, filters: Record<string, unknown> = {}) =>
      ['documents', companyId, filters] as const,
    /** Documentos de una entidad concreta (proveedor, cliente, empleado…). */
    ofEntity: (companyId?: string, entityType?: string, entityId?: string) =>
      ['documents', 'of-entity', companyId, entityType, entityId] as const,
  },

  /**
   * Tier 0 SILTRA reference catalogs (CCAA, contract types TGSS, CNO occupations,
   * SEPE termination causes, Modelo 190 subclaves). NOT tenant-scoped: the backend
   * returns identical data for every company, so the key intentionally OMITS
   * companyId — sharing the cache across tenants is correct here (unlike e.g.
   * departments, which ARE per-tenant). Keyed only by endpoint.
   */
  catalog: {
    byEndpoint: (endpoint: string) => ['catalog', endpoint] as const,
  },

  // Catálogo de verticales (GET /api/verticals) — estático por release.
  verticals: {
    all: () => ['verticals'] as const,
  },

  // Incidencias de soporte del propio usuario (GET /api/bug-reports). El backend
  // autolimita el listado por `user_id` salvo para admin/owner, así que la clave
  // no lleva el usuario: dos usuarios distintos nunca comparten sesión ni caché.
  bugReport: {
    all: () => ['bug-report'] as const,
    list: (companyId?: string) => ['bug-report', companyId] as const,
  },

  // Listado de una entidad para selectores/asociaciones de cartera (productos,
  // proveedores…). Key compartida por EntitySelect, AssociationManager y la
  // bandeja del Studio para deduplicar la caché.
  entitySelect: (entityType?: string, companyId?: string) => ['entity-select', entityType, companyId] as const,

  /**
   * Un ajuste de empresa de `company_settings`, por su clave.
   *
   * La clave va DENTRO de la clave de cache: dos ajustes distintos son dos
   * entradas distintas, e invalidar uno no debe vaciar el otro.
   */
  companySetting: (companyId?: string, key?: string) =>
    ['company-setting', companyId, key] as const,
} as const;
